/**
 * Controle de rastreadores — a lista de trabalho da equipe.
 *
 * Cruza o produto da placa (o plano diz que ela tem rastreador) com a
 * classificação do veículo (a equipe marcou que instalou). A regra de cada caso
 * mora no servidor — ver routes/rastreamento.ts; aqui só se escolhe o recorte.
 *
 * A tela é organizada pela pergunta da equipe, em ordem:
 *  1. o que está pendente e há quanto tempo — "falta instalar" com o tempo
 *     desde o cadastro em faixas, que também filtram a lista;
 *  2. a lista para trabalhar, com uma aba por caso;
 *  3. o retrato da carteira no SGA, por último, porque é consulta e não ação.
 *
 * Tocar numa placa abre os detalhes num modal — contato, endereço e veículo,
 * o que a equipe precisa para ligar e agendar a instalação sem abrir o SGA. A
 * lista fica onde estava por baixo, então fechar devolve a pessoa ao mesmo
 * ponto.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ChevronRight,
  Clock,
  Copy,
  FileDown,
  FlaskConical,
  Mail,
  MapPin,
  MessageCircle,
  PackageX,
  Phone,
  Printer,
  Radar,
  Search,
  Send,
  Undo2,
  Wrench,
  X,
} from 'lucide-react';
import {
  formatarCep,
  formatarCpf,
  formatarTelefone,
  moeda,
  numero,
} from '../../lib/format.ts';
import { exportarPdf, exportarPlanilha, exportarTexto } from '../../lib/exportar.ts';
import {
  Aviso,
  Botao,
  CaixaSelecao,
  Card,
  Esqueleto,
  EstadoVazio,
  Segmentado,
} from '../../components/ui/index.tsx';
import Folha from '../../components/ui/Folha.tsx';
import { CartaoDado } from '../../components/ui/Bento.tsx';
import { cx } from '../../lib/cx.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import FolhaComunicado, { MarcaComunicado } from './Comunicado.tsx';
import {
  CASOS,
  contatoPrincipal,
  dataBr,
  enderecoCompleto,
  rua,
  TOM_FAIXA,
  useDadosRastreamento,
  type Caso,
  type Faixa,
  type Item,
  type Associado,
  type Resposta,
} from './dados.ts';

/** Linhas por página da lista. */
const POR_PAGINA = 30;

export default function Controle() {
  const [aba, setAba] = useState<Caso>('falta_instalar');
  const [faixa, setFaixa] = useState<Faixa | null>(null);
  const [busca, setBusca] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const { data, isLoading } = useDadosRastreamento();
  const { pode } = useAuth();
  const podeComunicar = pode('admin');
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [folhaComunicado, setFolhaComunicado] = useState(false);

  const selecao: Selecao | undefined = podeComunicar
    ? {
        marcadas,
        alternar: (p) =>
          setMarcadas((atual) => {
            const nova = new Set(atual);
            if (nova.has(p)) nova.delete(p);
            else nova.add(p);
            return nova;
          }),
        definir: (placas, marcar) =>
          setMarcadas((atual) => {
            const nova = new Set(atual);
            for (const p of placas) {
              if (marcar) nova.add(p);
              else nova.delete(p);
            }
            return nova;
          }),
      }
    : undefined;

  // A faixa só existe em "falta instalar"; trocar de aba a descarta. A seleção
  // também: placa marcada numa aba e escondida na outra seria enviada sem que
  // ninguém a visse na tela.
  const escolherAba = (c: Caso) => {
    setAba(c);
    setMarcadas(new Set());
    if (c !== 'falta_instalar') setFaixa(null);
  };
  const escolherFaixa = (f: Faixa) => {
    setAba('falta_instalar');
    setFaixa((atual) => (atual === f ? null : f));
  };

  const contagem = (c: Caso): number => (data?.itens ?? []).filter((i) => i.caso === c).length;

  const itens = useMemo(() => {
    const termo = busca.trim().toUpperCase();
    const termoPlaca = termo.replace(/[^A-Z0-9]/g, '');
    return (data?.itens ?? []).filter(
      (i) =>
        i.caso === aba &&
        (!faixa || i.faixa === faixa) &&
        (!termo ||
          (termoPlaca !== '' && (i.placa ?? '').includes(termoPlaca)) ||
          (i.associado.nome ?? '').toUpperCase().includes(termo)),
    );
  }, [data, aba, faixa, busca]);

  const rotuloFaixa = data?.resumo.semInstalar.faixas.find((f) => f.id === faixa)?.rotulo;
  const titulo = `${CASOS[aba].aba}${rotuloFaixa ? ` · ${rotuloFaixa}` : ''}`;

  async function copiarTexto() {
    const linhas = itens.map(
      (i) =>
        `• *${i.placa ?? 's/ placa'}* — ${i.veiculo ?? ''}\n` +
        `  ${i.associado.nome ?? 'Sem nome'} · ` +
        `${formatarTelefone(contatoPrincipal(i.associado)) || 'sem telefone'}\n` +
        `  ${enderecoCompleto(i.associado) ?? 'sem endereço'}\n` +
        (i.diasSemInstalar !== null
          ? `  ${i.diasSemInstalar} dias sem instalar (cadastro ${dataBr(i.dataCadastro)})`
          : `  ${i.classificacao ?? 'sem classificação'}${i.produtos ? ` · ${i.produtos}` : ''}`),
    );
    const cabecalho =
      `*Rastreamento — ${titulo}* — ${itens.length} placa(s)\n` +
      `_Gerado em ${new Date().toLocaleString('pt-BR')}_\n\n`;
    const r = await exportarTexto(cabecalho + linhas.join('\n\n'), 'Rastreamento');
    setAviso(
      r.compartilhado ? null : r.ok ? 'Lista copiada — é só colar no WhatsApp.' : 'Não consegui copiar.',
    );
  }

  function baixarPlanilha() {
    exportarPlanilha(
      [
        { chave: 'placa', titulo: 'Placa' },
        { chave: 'veiculo', titulo: 'Veículo' },
        { chave: 'tipo', titulo: 'Tipo' },
        { chave: 'situacao', titulo: 'Situação' },
        { chave: 'cadastro', titulo: 'Data de cadastro' },
        { chave: 'dias', titulo: 'Dias sem instalar' },
        { chave: 'classificacao', titulo: 'Classificação no SGA' },
        { chave: 'produtos', titulo: 'Produto de rastreador' },
        { chave: 'valor', titulo: 'Valor do produto' },
        { chave: 'caso', titulo: 'Caso' },
        { chave: 'associado', titulo: 'Associado' },
        { chave: 'cpf', titulo: 'CPF' },
        { chave: 'celular', titulo: 'Celular' },
        { chave: 'telefone', titulo: 'Telefone' },
        { chave: 'email', titulo: 'E-mail' },
        { chave: 'endereco', titulo: 'Endereço' },
        { chave: 'bairro', titulo: 'Bairro' },
        { chave: 'cidade', titulo: 'Cidade' },
        { chave: 'uf', titulo: 'UF' },
        { chave: 'cep', titulo: 'CEP' },
        { chave: 'cooperativa', titulo: 'Cooperativa' },
        { chave: 'consultor', titulo: 'Consultor' },
      ],
      itens.map((i) => ({
        placa: i.placa,
        veiculo: i.veiculo,
        tipo: i.tipo,
        situacao: i.situacao,
        cadastro: dataBr(i.dataCadastro),
        dias: i.diasSemInstalar ?? '',
        classificacao: i.classificacao,
        produtos: i.produtos,
        // Vírgula decimal: é o que o Excel em português entende como número.
        valor: i.valorProdutos === null ? '' : i.valorProdutos.toFixed(2).replace('.', ','),
        caso: CASOS[i.caso].rotulo,
        associado: i.associado.nome,
        cpf: formatarCpf(i.associado.cpf),
        celular: formatarTelefone(i.associado.celular),
        telefone: formatarTelefone(i.associado.telefone),
        email: i.associado.email,
        endereco: rua(i.associado),
        bairro: i.associado.bairro,
        cidade: i.associado.cidade,
        uf: i.associado.estado,
        cep: formatarCep(i.associado.cep),
        cooperativa: i.cooperativa,
        consultor: i.consultor,
      })),
      'rastreamento',
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((i) => (
          <Esqueleto key={i} className="h-24" />
        ))}
      </div>
    );
  }

  const r = data?.resumo;

  return (
    <div className="flex flex-col gap-5">
      {/* Cabeçalho que só aparece no PDF. */}
      <div className="hidden imprimir-visivel">
        <h1 className="text-lg font-semibold">Rastreamento — {titulo}</h1>
        <p className="text-[13px] text-[var(--muted)]">
          {itens.length} placas · {new Date().toLocaleString('pt-BR')}
        </p>
      </div>

      {/* ── 1. Pendências ── */}
      {r && (
        <section aria-labelledby="pendencias" className="imprimir-oculto flex flex-col gap-3">
          <h2 id="pendencias" className="text-sm font-medium text-[var(--fg)]">
            Pendências
          </h2>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <TempoSemInstalar
              resumo={r}
              ativo={aba === 'falta_instalar'}
              faixa={faixa}
              onAbrir={() => escolherAba('falta_instalar')}
              onFaixa={escolherFaixa}
            />
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
              <CartaoDado
                ativo={aba === 'instalado_sem_produto'}
                onClick={() => escolherAba('instalado_sem_produto')}
                rotulo="Instalado sem produto"
                valor={numero(r.instaladoSemProduto)}
                nota="rastreador sem cobrança"
                Icone={PackageX}
                tom="alerta"
              />
              <CartaoDado
                ativo={aba === 'recolher'}
                onClick={() => escolherAba('recolher')}
                rotulo="Recolher"
                valor={numero(r.recolher)}
                nota="saiu da carteira com rastreador"
                Icone={Undo2}
                tom="alerta"
              />
            </div>
          </div>
        </section>
      )}

      {/* ── 2. Lista ── */}
      <section aria-labelledby="lista" className="flex flex-col gap-3">
        <h2 id="lista" className="sr-only">
          Placas
        </h2>

        <div className="imprimir-oculto scroll-x -mx-4 px-4 md:mx-0 md:px-0">
          <Segmentado
            rotulo="Casos"
            valor={aba}
            onChange={escolherAba}
            opcoes={(Object.keys(CASOS) as Caso[])
              .filter((c) => c !== 'aguardando' || contagem(c) > 0)
              .map((c) => ({
                valor: c,
                rotulo: (
                  <>
                    {CASOS[c].aba}
                    <span className="num text-xs text-[var(--faint)]">{numero(contagem(c))}</span>
                  </>
                ),
              }))}
          />
        </div>

        <div className="imprimir-oculto flex flex-wrap items-center gap-2">
          <label className="relative w-full sm:w-64">
            <span className="sr-only">Buscar placa ou associado</span>
            <Search
              size={14}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[var(--faint)]"
            />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Placa ou associado"
              className="anel-foco h-9 w-full rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] pr-3 pl-8 text-base text-[var(--fg)] placeholder:text-[var(--faint)] sm:text-[13px]"
            />
          </label>
          {rotuloFaixa && (
            <button
              onClick={() => setFaixa(null)}
              className="anel-foco inline-flex h-9 items-center gap-1.5 rounded-[var(--r-full)] border border-[var(--fg)] px-3 text-[13px] font-medium text-[var(--fg)]"
            >
              {rotuloFaixa}
              <X size={13} aria-label="Remover filtro de tempo" />
            </button>
          )}
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Botao tamanho="sm" icone={<Copy size={14} />} onClick={() => void copiarTexto()}>
              WhatsApp
            </Botao>
            <Botao tamanho="sm" icone={<FileDown size={14} />} onClick={baixarPlanilha}>
              Planilha
            </Botao>
            <Botao tamanho="sm" icone={<Printer size={14} />} onClick={exportarPdf}>
              PDF
            </Botao>
            {podeComunicar && (
              <Botao
                tamanho="sm"
                variante="fantasma"
                icone={<FlaskConical size={14} />}
                onClick={() => setFolhaComunicado(true)}
              >
                Testar comunicado
              </Botao>
            )}
          </div>
        </div>

        {aviso && <Aviso>{aviso}</Aviso>}

        {itens.length === 0 ? (
          <Card className="p-2">
            <EstadoVazio
              icone={<Radar size={20} />}
              titulo="Nenhuma placa neste recorte"
              descricao="Ou está tudo em ordem aqui, ou os filtros acima estão restritos demais."
            />
          </Card>
        ) : (
          <Tabela itens={itens} comTempo={aba === 'falta_instalar'} selecao={selecao} />
        )}
      </section>

      {podeComunicar && marcadas.size > 0 && (
        <BarraSelecao
          quantidade={marcadas.size}
          onLimpar={() => setMarcadas(new Set())}
          onComunicar={() => setFolhaComunicado(true)}
        />
      )}

      {podeComunicar && (
        <FolhaComunicado
          placas={[...marcadas]}
          aberta={folhaComunicado}
          onFechar={() => setFolhaComunicado(false)}
          onEnviado={(msg) => {
            setFolhaComunicado(false);
            setMarcadas(new Set());
            setAviso(msg);
          }}
        />
      )}

      {/* ── 3. Retrato da carteira ── */}
      {r && r.porClassificacao.length > 0 && <Classificacoes resumo={r} />}
    </div>
  );
}

// ─────────────────────────── Peças ───────────────────────────

/**
 * Barra flutuante da seleção. Fica presa ao fundo da tela para a ação estar à
 * mão em qualquer ponto da lista.
 */
function BarraSelecao({
  quantidade,
  onLimpar,
  onComunicar,
}: {
  quantidade: number;
  onLimpar: () => void;
  onComunicar: () => void;
}) {
  return (
    <div
      className="imprimir-oculto fixed inset-x-0 z-20 flex justify-center px-4 md:left-[240px]"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
    >
      <div className="flex w-full max-w-xl items-center gap-2 rounded-[var(--r-lg)] border border-[var(--border-strong)] bg-[var(--surface)] p-2 pl-4 shadow-[0_8px_32px_rgb(0_0_0/0.18)]">
        <p className="flex-1 text-[13px] text-[var(--fg)]">
          <span className="num font-semibold">{numero(quantidade)}</span>{' '}
          {quantidade === 1 ? 'selecionada' : 'selecionadas'}
        </p>
        <Botao tamanho="sm" variante="fantasma" onClick={onLimpar}>
          Limpar
        </Botao>
        <Botao tamanho="sm" variante="primario" icone={<Send size={14} />} onClick={onComunicar}>
          Enviar comunicado
        </Botao>
      </div>
    </div>
  );
}

/**
 * O cartão principal: quantos faltam instalar e há quanto tempo.
 *
 * As faixas são uma barra empilhada e, embaixo, botões que filtram a lista — o
 * número de cada faixa é clicável porque a pergunta seguinte a "quantos estão
 * há mais de 30 dias?" é sempre "quais?".
 */
function TempoSemInstalar({
  resumo,
  ativo,
  faixa,
  onAbrir,
  onFaixa,
}: {
  resumo: Resposta['resumo'];
  ativo: boolean;
  faixa: Faixa | null;
  onAbrir: () => void;
  onFaixa: (f: Faixa) => void;
}) {
  const { faixas, maisAntigo, mediana } = resumo.semInstalar;
  const total = faixas.reduce((s, f) => s + f.placas, 0);

  return (
    <div
      className={cx(
        'flex flex-col gap-4 rounded-[20px] border border-[var(--border)] bg-[var(--surface)] p-4 transition-colors sm:p-5',
        ativo && 'selecionado',
      )}
    >
      <button onClick={onAbrir} className="anel-foco -m-1 rounded-[var(--r-md)] p-1 text-left">
        <div className="mb-3 flex items-center gap-2.5">
          <span
            aria-hidden
            className="grid size-8 place-items-center rounded-[10px] bg-[var(--danger-subtle)] text-[var(--danger)]"
          >
            <Wrench size={16} strokeWidth={2.2} />
          </span>
          <p className="text-[13px] font-medium text-[var(--muted)]">Falta instalar</p>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <p className="num text-[40px] leading-none font-semibold tracking-tight text-[var(--danger)]">
            {numero(resumo.faltaInstalar)}
          </p>
          <p className="text-xs text-[var(--faint)]">
            tem produto e não está marcado como instalado
          </p>
        </div>
      </button>

      {total > 0 && (
        <>
          <div className="flex h-2 overflow-hidden rounded-[var(--r-full)] bg-[var(--gray-100)]">
            {faixas.map((f) =>
              f.placas === 0 ? null : (
                <span
                  key={f.id}
                  style={{ width: `${(f.placas / total) * 100}%`, background: TOM_FAIXA[f.id] }}
                />
              ),
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {faixas.map((f) => (
              <button
                key={f.id}
                onClick={() => onFaixa(f.id)}
                aria-pressed={faixa === f.id}
                className={cx(
                  'anel-foco rounded-[var(--r-md)] border bg-[var(--surface)] px-3 py-2 text-left transition-colors',
                  faixa === f.id
                    ? 'selecionado'
                    : 'border-[var(--border)] hover:bg-[var(--surface-hover)]',
                )}
              >
                <span className="flex items-center gap-1.5 text-xs text-[var(--muted)]">
                  <span
                    aria-hidden
                    className="size-2 shrink-0 rounded-full"
                    style={{ background: TOM_FAIXA[f.id] }}
                  />
                  {f.rotulo}
                </span>
                <span className="num mt-0.5 block text-lg font-semibold text-[var(--fg)]">
                  {numero(f.placas)}
                </span>
              </button>
            ))}
          </div>

          <p className="flex items-center gap-1.5 text-xs text-[var(--faint)]">
            <Clock size={12} />
            Contado da data de cadastro · mediana {numero(mediana)} dias · mais antigo{' '}
            {numero(maisAntigo)} dias
          </p>
        </>
      )}
    </div>
  );
}

/** Como a carteira está classificada no SGA hoje — inclusive quem não tem rastreador. */
function Classificacoes({ resumo }: { resumo: Resposta['resumo'] }) {
  const maior = Math.max(...resumo.porClassificacao.map((c) => c.placas));
  return (
    <section aria-labelledby="classificacao" className="imprimir-oculto flex flex-col gap-3">
      <h2 id="classificacao" className="text-sm font-medium text-[var(--fg)]">
        Carteira no SGA
      </h2>
      <Card className="p-4">
        <p className="mb-3 text-[13px] text-[var(--muted)]">
          Classificação do veículo · {numero(resumo.carteira)} placas ·{' '}
          {numero(resumo.comProduto)} com produto de rastreador
        </p>
        <ul className="flex list-none flex-col gap-2 p-0">
          {resumo.porClassificacao.map((c) => (
            <li
              key={c.classificacao}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1"
            >
              <span className="truncate text-[13px] text-[var(--fg)]">{c.classificacao}</span>
              <span className="num text-right text-[13px] text-[var(--muted)]">
                {numero(c.placas)}
              </span>
              <span className="col-span-2 h-1.5 rounded-[var(--r-full)] bg-[var(--gray-100)]">
                <span
                  className="block h-full rounded-[var(--r-full)] bg-[var(--fg)]"
                  style={{ width: `${(c.placas / maior) * 100}%` }}
                />
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

/** Dias sem instalar, com a cor da faixa. */
function Dias({ item }: { item: Item }) {
  if (item.diasSemInstalar === null || !item.faixa) return null;
  return (
    <span className="num font-semibold" style={{ color: TOM_FAIXA[item.faixa] }}>
      {numero(item.diasSemInstalar)}d
    </span>
  );
}

/**
 * Tabela no desktop, cartões no celular — mesma regra dos inadimplentes: muitas
 * colunas em 360px não se leem nem rolando.
 *
 * Em "falta instalar" a coluna de tempo assume o lugar da classificação, que
 * ali é redundante (é sempre não informado ou retirado).
 *
 * Detalhes em modal, não expandindo no lugar: com três blocos de campos, a
 * linha aberta empurrava a lista para baixo e a pessoa perdia onde estava.
 */
/** Seleção de placas para o comunicado — só existe para quem pode enviar. */
interface Selecao {
  marcadas: Set<string>;
  alternar: (placa: string) => void;
  definir: (placas: string[], marcar: boolean) => void;
}

function Tabela({
  itens: todas,
  comTempo,
  selecao,
}: {
  itens: Item[];
  comTempo: boolean;
  selecao?: Selecao;
}) {
  const [detalhe, setDetalhe] = useState<Item | null>(null);
  const fecharDetalhe = useCallback(() => setDetalhe(null), []);
  // Página em vez de lista inteira: centenas de linhas pesam no celular e
  // empurram o resto da tela para longe. A lista já vem na ordem de
  // atendimento, então as primeiras são as que importam.
  const [limite, setLimite] = useState(POR_PAGINA);
  useEffect(() => setLimite(POR_PAGINA), [todas]);
  const itens = todas.slice(0, limite);
  const restantes = todas.length - itens.length;
  const chave = (i: Item) => i.placa ?? '';

  // "Todas" é o recorte inteiro, não só a página visível: quem filtra "mais de
  // 30 dias" e marca tudo espera pegar as 55, não as 30 da tela.
  const placasRecorte = todas.map(chave).filter(Boolean);
  const marcadasNoRecorte = selecao
    ? placasRecorte.filter((p) => selecao.marcadas.has(p)).length
    : 0;
  const recorteTodo = marcadasNoRecorte > 0 && marcadasNoRecorte === placasRecorte.length;

  return (
    <>
      {selecao && (
        <div className="imprimir-oculto -mt-1 -ml-2 flex items-center text-[13px] text-[var(--muted)]">
          <CaixaSelecao
            marcada={recorteTodo}
            indeterminada={marcadasNoRecorte > 0 && !recorteTodo}
            onChange={() => selecao.definir(placasRecorte, !recorteTodo)}
            rotulo="Selecionar todas do recorte"
          />
          Selecionar todas ({numero(placasRecorte.length)})
        </div>
      )}

      <div className="hidden md:block">
        {/* A rolagem vive dentro do cartão, não na página. */}
        <Card className="scroll-x">
          <table className="w-full min-w-[760px] text-[13px]">
            <thead className="border-b border-[var(--border)] bg-[var(--bg-subtle)] text-left">
              <tr className="text-[var(--muted)]">
                {selecao && (
                  <th className="w-12 py-3 pl-2">
                    <span className="sr-only">Selecionar</span>
                  </th>
                )}
                <th className="p-3 font-medium">Placa</th>
                <th className="p-3 font-medium">Associado</th>
                {comTempo ? (
                  <th className="p-3 text-right font-medium">Sem instalar</th>
                ) : (
                  <th className="p-3 font-medium">Classificação</th>
                )}
                <th className="p-3 font-medium">Situação</th>
                <th className="p-3 font-medium">Cooperativa</th>
                <th className="w-10 p-3">
                  <span className="sr-only">Detalhes</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr
                  key={chave(i)}
                  onClick={() => setDetalhe(i)}
                  className={cx(
                    'group cursor-pointer border-b border-[var(--border)] transition-colors last:border-0 hover:bg-[var(--surface-hover)]',
                    selecao?.marcadas.has(chave(i)) && 'bg-[var(--brand-subtle)]',
                  )}
                >
                  {selecao && (
                    <td className="py-1.5 pl-2 align-top">
                      <CaixaSelecao
                        marcada={selecao.marcadas.has(chave(i))}
                        onChange={() => selecao.alternar(chave(i))}
                        rotulo={`Selecionar ${i.placa ?? 'placa'}`}
                      />
                    </td>
                  )}
                  <td className="p-3 align-top">
                    <p className="num font-medium whitespace-nowrap text-[var(--fg)]">
                      {i.placa ?? '—'}
                    </p>
                    <p className="max-w-[180px] truncate text-xs text-[var(--faint)]">
                      {i.veiculo ?? '—'}
                    </p>
                    <MarcaComunicado item={i} />
                  </td>
                  <td className="p-3 align-top">
                    <p
                      className="max-w-[260px] truncate text-[var(--fg)]"
                      title={i.associado.nome ?? undefined}
                    >
                      {i.associado.nome ?? '—'}
                    </p>
                    <p className="num text-xs text-[var(--faint)]">
                      {formatarTelefone(contatoPrincipal(i.associado)) || 'sem telefone'}
                    </p>
                    <p className="max-w-[260px] truncate text-xs text-[var(--faint)]">
                      {[i.associado.bairro, i.associado.cidade].filter(Boolean).join(' · ')}
                    </p>
                  </td>
                  {comTempo ? (
                    <td className="p-3 text-right align-top">
                      <Dias item={i} />
                      <span className="num block text-xs text-[var(--faint)]">
                        desde {dataBr(i.dataCadastro)}
                      </span>
                    </td>
                  ) : (
                    <td className="p-3 align-top text-[var(--muted)]">{i.classificacao ?? '—'}</td>
                  )}
                  <td className="p-3 align-top text-[var(--muted)]">{i.situacao ?? '—'}</td>
                  <td className="p-3 align-top text-[var(--muted)]">
                    <span className="block max-w-[200px] truncate" title={i.cooperativa ?? undefined}>
                      {i.cooperativa ?? '—'}
                    </span>
                    {i.consultor && (
                      <span
                        className="block max-w-[200px] truncate text-xs text-[var(--faint)]"
                        title={i.consultor}
                      >
                        {i.consultor}
                      </span>
                    )}
                  </td>
                  <td className="p-3 align-top">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetalhe(i);
                      }}
                      aria-label={`Ver detalhes de ${i.placa ?? 'placa'}`}
                      className="anel-foco grid size-7 place-items-center rounded-[var(--r-md)] text-[var(--faint)] group-hover:text-[var(--fg)] hover:bg-[var(--surface-hover)]"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <ul className="flex list-none flex-col gap-3 p-0 md:hidden">
        {itens.map((i) => (
          <Card
            as="li"
            key={chave(i)}
            className={cx('p-4 transition-colors', selecao?.marcadas.has(chave(i)) && 'selecionado')}
          >
            <div className="flex items-start gap-2">
              {selecao && (
                // Alvo de 40px com a caixa alinhada à primeira linha (a placa).
                <CaixaSelecao
                  className="-my-2 -ml-2"
                  marcada={selecao.marcadas.has(chave(i))}
                  onChange={() => selecao.alternar(chave(i))}
                  rotulo={`Selecionar ${i.placa ?? 'placa'}`}
                />
              )}
              <button
                onClick={() => setDetalhe(i)}
                aria-haspopup="dialog"
                className="anel-foco -m-1 block min-w-0 flex-1 rounded-[var(--r-md)] p-1 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="num font-medium text-[var(--fg)]">{i.placa ?? '—'}</p>
                    <p className="truncate text-xs text-[var(--faint)]">{i.veiculo ?? '—'}</p>
                  </div>
                  <div className="flex shrink-0 items-start gap-1.5 text-right">
                    {comTempo ? (
                      <div>
                        <Dias item={i} />
                        <p className="num text-xs text-[var(--faint)]">{dataBr(i.dataCadastro)}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--muted)]">{i.situacao ?? '—'}</p>
                    )}
                    <ChevronRight size={18} className="mt-0.5 text-[var(--faint)]" />
                  </div>
                </div>
                <p className="mt-2 truncate text-[13px] text-[var(--fg)]">
                  {i.associado.nome ?? '—'}
                </p>
                <p className="mt-0.5 truncate text-xs text-[var(--muted)]">
                  {[i.associado.bairro, i.associado.cidade].filter(Boolean).join(' · ') ||
                    'sem endereço'}
                </p>
                <MarcaComunicado item={i} />
              </button>
            </div>

            <Acoes associado={i.associado} compacto className="mt-3" />
          </Card>
        ))}
      </ul>

      {restantes > 0 && (
        <div className="imprimir-oculto flex flex-wrap items-center justify-center gap-2">
          <Botao tamanho="sm" onClick={() => setLimite((l) => l + POR_PAGINA)}>
            Mostrar mais {numero(Math.min(POR_PAGINA, restantes))}
          </Botao>
          <Botao tamanho="sm" variante="fantasma" onClick={() => setLimite(todas.length)}>
            Mostrar todas ({numero(todas.length)})
          </Botao>
        </div>
      )}

      <Folha
        aberta={detalhe !== null}
        larga
        titulo={detalhe ? [detalhe.placa, detalhe.associado.nome].filter(Boolean).join(' · ') : ''}
        onFechar={fecharDetalhe}
      >
        {detalhe && (
          <div className="flex flex-col gap-4 p-1">
            {comTempo && detalhe.diasSemInstalar !== null && (
              <p className="text-[13px] text-[var(--muted)]">
                <Dias item={detalhe} /> sem instalar · cadastrada em {dataBr(detalhe.dataCadastro)}
              </p>
            )}
            <Detalhes item={detalhe} />
            {selecao && detalhe.placa && (
              <div className="-ml-2 flex items-center border-t border-[var(--border)] pt-2 text-[13px] text-[var(--fg)]">
                <CaixaSelecao
                  marcada={selecao.marcadas.has(detalhe.placa)}
                  onChange={() => selecao.alternar(detalhe.placa!)}
                  rotulo="Incluir no comunicado"
                />
                Incluir no comunicado de suspensão
              </div>
            )}
          </div>
        )}
      </Folha>
    </>
  );
}

/** Os detalhes da placa: quem contatar, onde ir e o que está no veículo. */
function Detalhes({ item: i }: { item: Item }) {
  const a = i.associado;
  return (
    <div className="grid gap-4 text-[13px] md:grid-cols-3">
      {/* Atalhos primeiro: na rua, ligar é o que se quer ao abrir a placa. */}
      <Acoes associado={a} className="md:col-span-3" />

      <Bloco titulo="Associado">
        <Campo rotulo="Nome" valor={a.nome} />
        <Campo rotulo="CPF" valor={a.cpf ? formatarCpf(a.cpf) : null} num />
        <Campo rotulo="Celular" valor={a.celular ? formatarTelefone(a.celular) : null} num />
        <Campo rotulo="Telefone" valor={a.telefone ? formatarTelefone(a.telefone) : null} num />
        <Campo rotulo="E-mail" valor={a.email} />
      </Bloco>

      <Bloco titulo="Localização">
        <Campo rotulo="Endereço" valor={rua(a)} />
        <Campo rotulo="Bairro" valor={a.bairro} />
        <Campo
          rotulo="Cidade"
          valor={[a.cidade, a.estado].filter(Boolean).join('/') || null}
        />
        <Campo rotulo="CEP" valor={a.cep ? formatarCep(a.cep) : null} num />
      </Bloco>

      <Bloco titulo="Veículo">
        <Campo rotulo="Modelo" valor={[i.veiculo, i.ano].filter(Boolean).join(' · ') || null} />
        <Campo rotulo="Tipo" valor={i.tipo} />
        <Campo rotulo="Chassi" valor={i.chassi} num />
        <Campo rotulo="Renavam" valor={i.renavam} num />
        <Campo rotulo="FIPE protegida" valor={i.valorFipe !== null ? moeda(i.valorFipe) : null} num />
        <Campo rotulo="Cadastro" valor={dataBr(i.dataCadastro)} num />
        <Campo rotulo="Situação" valor={i.situacao} />
        <Campo rotulo="Classificação" valor={i.classificacao} />
        <Campo
          rotulo="Rastreador"
          valor={
            i.produtos
              ? `${i.produtos}${i.valorProdutos ? ` · ${moeda(i.valorProdutos)}` : ''}`
              : 'nenhum produto'
          }
        />
      </Bloco>
    </div>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="mb-2 text-xs font-medium tracking-wide text-[var(--faint)] uppercase">
        {titulo}
      </p>
      <dl className="flex flex-col gap-1.5">{children}</dl>
    </div>
  );
}

function Campo({ rotulo, valor, num }: { rotulo: string; valor: string | null; num?: boolean }) {
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2">
      <dt className="text-[var(--muted)]">{rotulo}</dt>
      <dd className={cx('break-words text-[var(--fg)]', num && 'num')}>{valor ?? '—'}</dd>
    </div>
  );
}

/**
 * Atalhos de contato. O WhatsApp abre direto na conversa (wa.me com DDI 55) e
 * o mapa pesquisa o endereço como está no cadastro — sem geocodificar nada
 * aqui, o que exigiria chave de API e mandaria o endereço do associado para
 * terceiros a cada carregamento.
 */
function Acoes({
  associado: a,
  className,
  compacto,
}: {
  associado: Associado;
  className?: string;
  /** No cartão da lista: só ligar, WhatsApp e mapa, numa linha. */
  compacto?: boolean;
}) {
  const cel = a.celular?.replace(/\D/g, '');
  const tel = contatoPrincipal(a)?.replace(/\D/g, '');
  const endereco = enderecoCompleto(a);
  if (!tel && !endereco && !a.email) return null;

  const estilo = cx(
    'anel-foco inline-flex h-10 items-center justify-center gap-1.5 rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-[13px] font-medium text-[var(--fg)] hover:bg-[var(--surface-hover)]',
    compacto && 'min-w-0 flex-1',
  );

  return (
    <div className={cx('imprimir-oculto flex gap-2', !compacto && 'flex-wrap', className)}>
      {tel && (
        <a href={`tel:${tel}`} className={estilo} onClick={(e) => e.stopPropagation()}>
          <Phone size={14} /> Ligar
        </a>
      )}
      {cel && (
        <a
          href={`https://wa.me/55${cel}`}
          target="_blank"
          rel="noreferrer"
          className={estilo}
          onClick={(e) => e.stopPropagation()}
        >
          <MessageCircle size={14} /> WhatsApp
        </a>
      )}
      {endereco && (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`}
          target="_blank"
          rel="noreferrer"
          className={estilo}
          onClick={(e) => e.stopPropagation()}
        >
          <MapPin size={14} /> Mapa
        </a>
      )}
      {a.email && !compacto && (
        <a href={`mailto:${a.email}`} className={estilo} onClick={(e) => e.stopPropagation()}>
          <Mail size={14} /> E-mail
        </a>
      )}
    </div>
  );
}
